import type { WebTabKey } from '@/hooks/useWebTabRouter'
import type { ValidationError } from '@/utils/errorHandling'

/**
 * Which tab owns each field the sign-off gate can complain about.
 *
 * Strict and tab-level by design. Keyed on the root of the server's dotted FieldName
 * ("OnlineEvent.TimeZone" -> "OnlineEvent"), with the two-segment prefixes in FIELD_PREFIX_TO_TAB
 * checked first ("Download.Quiz.Bands" -> "Download.Quiz", which lives on its own tab while the rest
 * of "Download" stays on Details), and anything not listed here resolves to no tab at
 * all — the item still renders, it just renders without a link. A default would be worse than no
 * link: it would send the user confidently to a screen that does not contain the field.
 *
 * Verified against where each field is actually edited: WebDetailsTab hosts the online-event,
 * speaker, download, meeting and demo panels, and emails are rewritten or deleted on the Assets tab. Sub-section deep links (scrolling to the speakers card)
 * are deliberately out of scope — web tabs are path-addressable, sub-sections are internal state.
 *
 * Slug has no entry (ORB-482). The server stopped listing it as outstanding once the sign-off panel
 * began collecting it, so nothing routes here for it. Leaving the entry behind would quietly keep
 * "go to Settings" as the answer for a field the flow now asks for at the end.
 */
/**
 * A Map, not an object literal, because the key comes off the wire. Indexing an object with a
 * server-supplied string walks the prototype chain, so a FieldName of "constructor" or "toString"
 * returns a function rather than undefined — truthy, so `?? null` never fires and the caller treats
 * it as a real tab. A Map has no such fallthrough: unknown keys are always undefined, which is what
 * "strict allowlist" is supposed to mean.
 */
const FIELD_ROOT_TO_TAB = new Map<string, WebTabKey>([
    ['OnlineEvent', 'details'],
    ['Speakers', 'details'],
    ['Download', 'details'],
    ['Meeting', 'details'],
    ['Demo', 'details'],
    ['LandingPage', 'landing-page'],
    ['Assets', 'media'],
])

/** First two segments of the FieldName. Compared whole, so "Download.Quizzes" still falls to its root. */
const FIELD_PREFIX_TO_TAB = new Map<string, WebTabKey>([
    ['Download.Quiz', 'quiz'],
])

/** Human label for the tab an outstanding item routes to. Same reasoning — see above. */
const TAB_LABEL = new Map<WebTabKey, string>([
    ['details', 'Details'],
    ['quiz', 'Quiz'],
    ['settings', 'Settings'],
    ['activations', 'Activations'],
    ['email-sends', 'Email Sends'],
    ['meetings', 'Meetings'],
    ['calendar', 'Calendar'],
    ['leads', 'Leads'],
    ['media', 'Assets'],
    ['landing-page', 'Landing Page'],
])

export interface ReadinessItem {
    /** The server's message, rendered verbatim — the dashboard owns no readiness vocabulary. */
    message: string
    /** Tab that owns the field, or null when the field name is not one we can place. */
    tab: WebTabKey | null
    tabLabel: string | null
}

/**
 * Resolve the tab that owns a field. Returns null for an unrecognised or absent field name, which
 * the UI renders as plain text rather than a link.
 */
export function resolveReadinessTab(fieldName: string | undefined | null): WebTabKey | null {
    if (!fieldName) return null
    const segments = fieldName.split('.')
    return FIELD_PREFIX_TO_TAB.get(segments.slice(0, 2).join('.')) ?? FIELD_ROOT_TO_TAB.get(segments[0]) ?? null
}

/** Project the server's outstanding list into what the chip and popover render. */
export function toReadinessItems(outstanding: ValidationError[] | undefined): ReadinessItem[] {
    return (outstanding || []).map(error => {
        const tab = resolveReadinessTab(error.FieldName)
        return { message: error.Message, tab, tabLabel: tab ? TAB_LABEL.get(tab) ?? null : null }
    })
}

/** One-line summary for a native `title` tooltip in the webs list. */
export function summariseOutstanding(outstanding: ValidationError[] | undefined): string {
    return (outstanding || []).map(error => error.Message).join(' · ')
}

/**
 * Whether the public title is still unset.
 *
 * Asked of the readiness list rather than the title itself because the API's GetTitle() falls back to
 * the Web's Name, so a title-less web arrives carrying its internal reference code in the Title field
 * and the two are indistinguishable downstream. The readiness list is the only thing that still knows
 * the difference. Returns false when the list is absent — unknown is not the same as missing.
 */
export function isTitleOutstanding(outstanding: ValidationError[] | undefined): boolean {
    return (outstanding || []).some(error => error.FieldName?.endsWith('.Title') === true)
}
